import { measureAndLog } from '@hotjar/performance';
import { MARKS } from '@hotjar/webapp/performance';
import type { Store } from '../../../store/player/types';

export const maybeLogDatadogRum = (store: Store): void => {
  const {
    isLightPlayer,
    isRetakerMode,
    lastTickTime,
    jumpToPause,
    playTickInterval,
    playbackSpeedLogCount,
    playbackSpeedTotalLog,
    timelineJumpToTime,
    isFirstTimelineJump,
    playTickCount,
    recording,
    skipPauses,
    playbackSpeed,
    manuallyPausedOnce,
    setLastTickTime,
    incrementPlayTickCount,
    resetPlaybackSpeedLog,
    incrementPlaybackSpeedLog,
    setFirstTimelineJump,
    setTimelineJumpToTime,
    hasRegisteredFirstTick,
    setHasRegisteredFirstTick,
  } = store.getState();

  if (isLightPlayer || !recording || isRetakerMode || !window.datadogRum) {
    return;
  }

  const { duration } = recording;

  // Datadog RUM playback tick performance tracking.
  if (!skipPauses && !jumpToPause) {
    if (lastTickTime) {
      const playTickDelay = playTickInterval / playbackSpeed;
      const actualPlaybackSpeed = performance.now() - lastTickTime;
      const delta = actualPlaybackSpeed - playTickDelay;

      incrementPlaybackSpeedLog(playbackSpeed, delta);
    }

    setLastTickTime(performance.now());
  }

  if (document.visibilityState === 'hidden') {
    // If player is in background it is slower so we don't want to measure that.
    setLastTickTime(null);
  }

  // Send playback tick average after every 5% of recording has played
  const tickCountBeforeLogging = (duration / playTickInterval) * 0.05;
  if (playbackSpeedLogCount[playbackSpeed] > tickCountBeforeLogging) {
    const eventName = `playback_speed_${playbackSpeed.toString().replace('.', '')}x_delta_average`;
    const average = playbackSpeedTotalLog[playbackSpeed] / playbackSpeedLogCount[playbackSpeed];
    window.datadogRum.addAction('playback-speed', {
      [eventName]: average,
    });

    setLastTickTime(null);

    // Reset log.
    resetPlaybackSpeedLog(playbackSpeed);
  }

  // Datadog RUM timeline jump performance logging.
  if (timelineJumpToTime !== null) {
    const timelinePerformance = Math.floor(performance.now() - timelineJumpToTime);
    if (isFirstTimelineJump) {
      setFirstTimelineJump();
      window.datadogRum.addAction('first-timeline-jump', {
        first_timeline_jump: timelinePerformance,
      });
    }
    window.datadogRum.addAction('timeline-jump', {
      timeline_jump: timelinePerformance,
    });

    setTimelineJumpToTime(null);
  }

  // Datadog RUM initial load tracking.
  if (playTickCount === 1 && !manuallyPausedOnce && document.visibilityState === 'visible') {
    // If recording got loaded in the background ignore.

    if (!hasRegisteredFirstTick) {
      // Log when playback tick has ended in datadog RUM.
      measureAndLog({
        name: MARKS.PLAYBACKS_RECORDING_FIRST_TICK,
        extra: {
          // After validating if the new metrics work correctly, we should deprecate the 'first_tick' param and use event duration instead
          first_tick: Math.floor(performance.now()),
          isFirstRecording: true,
        },
      });
      setHasRegisteredFirstTick(true);
    } else {
      const startMark = performance.getEntriesByName(MARKS.PLAYBACKS_NEXT_RECORDING_FETCH_START)[0];
      const firstTickTime = Math.floor(performance.now()) - Math.floor(startMark.startTime);
      measureAndLog({
        name: MARKS.PLAYBACKS_RECORDING_FIRST_TICK,
        startMark: MARKS.PLAYBACKS_NEXT_RECORDING_FETCH_START,
        extra: {
          first_tick: firstTickTime,
          isFirstRecording: false,
        },
      });
    }
  }

  incrementPlayTickCount();
};
